import { z } from "zod";
import {
  ExperimentType,
  ExperimentUnitType,
  Phase,
  Surface,
  AssignmentMode,
  ExposureTracking,
  EligibilityPersistence,
  RuleSubtype,
} from "../types/experiment";

export const experimentTypeSchema = z.nativeEnum(ExperimentType);
export const experimentUnitTypeSchema = z.nativeEnum(ExperimentUnitType);
export const phaseSchema = z.nativeEnum(Phase);
export const surfaceSchema = z.nativeEnum(Surface);
export const assignmentModeSchema = z.nativeEnum(AssignmentMode);
export const exposureTrackingSchema = z.nativeEnum(ExposureTracking);
export const eligibilityPersistenceSchema = z.nativeEnum(EligibilityPersistence);
export const ruleSubtypeSchema = z.nativeEnum(RuleSubtype);

export const timestampSchema = z.object({
  seconds: z.string(),
  nanos: z.number().int(),
});

export const fixed64ValueSchema = z.object({
  value: z.string(),
});

export const uint32ValueSchema = z.object({
  value: z.number().int(),
});

export const uint64ValueSchema = z.object({
  value: z.string(),
});

export const versionBoundSchema = z.object({
  version: z.object({
    major: z.number().int().min(0),
    minor: z.number().int().min(0).optional(),
    build: z.string().optional(),
  }).optional(),
  inclusive: z.boolean(),
});

export const platformVersionRangeSchema = z.object({
  lower_bound: versionBoundSchema.optional(),
  upper_bound: versionBoundSchema.optional(),
});

export const platformVersionSchema = z.object({
  ranges: z.array(platformVersionRangeSchema),
  work_around_pyoto_bug: z.boolean(),
});

export const sdkVersionRangeBoundSchema = z.object({
  version: z.object({
    version: z.number().int().min(0),
  }).optional(),
  inclusive: z.boolean(),
});

export const sdkVersionRangeSchema = z.object({
  lower_bound: sdkVersionRangeBoundSchema.optional(),
  upper_bound: sdkVersionRangeBoundSchema.optional(),
});

export const sdkVersionSchema = z.object({
  ranges: z.array(sdkVersionRangeSchema),
  work_around_pyoto_bug: z.boolean(),
});

export const clientPlatformSchema = z.object({
  ios_version: platformVersionSchema.optional(),
  android_version: platformVersionSchema.optional(),
  web_version: platformVersionSchema.optional(),
  native_version: platformVersionSchema.optional(),
  allow_non_native_web: z.boolean(),
  client_required_changes: z.object({
    commit_hashes: z.array(z.string()),
    pr_numbers: z.array(z.number().int()),
  }).optional(),
});

export const clientOperatingSystemSchema = z.object({
  ios_version: sdkVersionSchema.optional(),
  android_version: sdkVersionSchema.optional(),
  macos_version: sdkVersionSchema.optional(),
  windows_version: sdkVersionSchema.optional(),
  playstation_version: sdkVersionSchema.optional(),
  xbox_version: sdkVersionSchema.optional(),
  linux_version: sdkVersionSchema.optional(),
});

export const alwaysSchema = z.object({ value: z.boolean() });

export const userIdsSchema = z.object({ user_ids: z.array(z.string()) });

export const guildIdsSchema = z.object({ guild_ids: z.array(z.string()) });

export const userAgeRangeSchema = z.object({
  min_age_years: z.number().int().min(0).optional(),
  max_age_years: z.number().int().min(0).optional(),
});

export const userIdRangeSchema = z.object({
  min_id: fixed64ValueSchema.optional(),
  max_id: fixed64ValueSchema.optional(),
});

export const guildIdRangeSchema = z.object({
  min_id: fixed64ValueSchema.optional(),
  max_id: fixed64ValueSchema.optional(),
});

export const guildMemberCountRangeSchema = z.object({
  min_count: z.number().int().min(0).optional(),
  max_count: z.number().int().min(0).optional(),
});

export const userInGuildSchema = z.object({
  guild_ids: z.array(z.string()),
});

export const locationSchema: z.ZodType<any> = z.union([
  z.object({
    iso_region: z.object({ iso_country: z.string(), iso_subdivision: z.string() }),
  }),
  z.object({ is_eu: z.boolean() }),
  z.object({
    place: z.object({ city: z.string(), subdivision: z.string(), country: z.string() }),
  }),
]);

export const userLocationSchema = z.object({
  locations: z.array(locationSchema),
  prefer_client_ip: z.boolean(),
});

export const clientLocationSchema = z.object({
  locations: z.array(locationSchema),
});

export const userIpSchema = z.object({
  blocks: z.array(z.string()),
  prefer_client_ip: z.boolean(),
});

export const clientIpSchema = z.object({
  blocks: z.array(z.string()),
});

export const userLocaleSchema = z.object({
  locales: z.array(z.string()),
});

export const clientLocaleSchema = z.object({
  locales: z.array(z.string()),
});

export const userHasFlagSchema = z.object({
  mask: z.string(),
});

export const staffUsersSchema = z.object({
  work_accounts: z.boolean(),
  personal_accounts: z.boolean(),
});

export const userIsBotSchema = z.object({
  is_bot: z.boolean(),
});

export const guildHasFeatureSchema = z.object({
  features: z.array(z.string()),
});

export const installationIdsSchema = z.object({
  installation_ids: z.array(z.string()),
});

export const unitIdInExperimentSchema = z.object({
  experiment_id: z.string(),
  variation_ids: z.array(z.number().int()),
});

export const clientReleaseChannelSchema = z.object({
  release_channels: z.array(z.string()),
});

export const filterSchema: z.ZodType<any> = z.lazy(() =>
  z.union([
    z.object({ client_version: clientPlatformSchema, negate: z.boolean().optional() }),
    z.object({ client_os: clientOperatingSystemSchema, negate: z.boolean().optional() }),
    z.object({ staff: staffUsersSchema, negate: z.boolean().optional() }),
    z.object({ user_in_guild: userInGuildSchema, negate: z.boolean().optional() }),
    z.object({ user_ids: userIdsSchema, negate: z.boolean().optional() }),
    z.object({ client_locale: clientLocaleSchema, negate: z.boolean().optional() }),
    z.object({ client_location: clientLocationSchema, negate: z.boolean().optional() }),
    z.object({ client_ip: clientIpSchema, negate: z.boolean().optional() }),
    z.object({ user_locale: userLocaleSchema, negate: z.boolean().optional() }),
    z.object({ bot: userIsBotSchema, negate: z.boolean().optional() }),
    z.object({ user_age_range: userAgeRangeSchema, negate: z.boolean().optional() }),
    z.object({ user_id_range: userIdRangeSchema, negate: z.boolean().optional() }),
    z.object({ user_has_flag: userHasFlagSchema, negate: z.boolean().optional() }),
    z.object({ client_release_channel: clientReleaseChannelSchema, negate: z.boolean().optional() }),
    z.object({ always: alwaysSchema, negate: z.boolean().optional() }),
    z.object({ unit_id_in_experiment: unitIdInExperimentSchema, negate: z.boolean().optional() }),
    z.object({ guild_ids: guildIdsSchema, negate: z.boolean().optional() }),
    z.object({ guild_id_range: guildIdRangeSchema, negate: z.boolean().optional() }),
    z.object({ guild_member_count_range: guildMemberCountRangeSchema, negate: z.boolean().optional() }),
    z.object({ guild_has_feature: guildHasFeatureSchema, negate: z.boolean().optional() }),
    z.object({ user_location: userLocationSchema, negate: z.boolean().optional() }),
    z.object({ user_ip: userIpSchema, negate: z.boolean().optional() }),
    z.object({ installation_ids: installationIdsSchema, negate: z.boolean().optional() }),
  ]),
);

export const overrideSchema = z.object({
  variation_id: z.number().int(),
});

export const bucketSchema = z.object({
  start: z.number(),
  stop: z.number(),
  type: experimentTypeSchema,
});

export const variationSchema = z.object({
  id: z.number().int(),
  label: z.string(),
  target_allocation: z.number(),
  buckets: z.array(bucketSchema),
  type: experimentTypeSchema,
  configuration: z.string().optional(),
  owning_experiment_id: z.string(),
});

export const ruleSchema = z.object({
  type: experimentTypeSchema,
  filters: z.array(filterSchema),
  override: overrideSchema.optional(),
  is_sunset_rule: z.boolean(),
  subtype: ruleSubtypeSchema,
  hash: z.string(),
});

export const debugConfigSchema = z.object({
  enable_decision_logging: z.boolean(),
  metrics_sample_rate: z.number(),
  log_context_on_failure: z.boolean(),
  log_raw_headers: z.boolean(),
  tag_filter_metrics: z.boolean(),
  decision_log_sample_rate: z.number(),
});

export const numberLineSettingsSchema = z.object({
  mode: z.number().int(),
  linked_id: z.string(),
  shared_control: z.boolean(),
});

export const experimentSchema = z.object({
  id: z.string(),
  name: z.string(),
  created_at: timestampSchema.optional(),
  creator_id: z.string(),
  version: z.number().int(),
  edited_at: timestampSchema.optional(),
  editor_id: z.string(),
  title: z.string(),
  description: z.string(),
  hypothesis: z.string().optional(),
  tech_spec_link: z.string().optional(),
  revision: z.number().int(),
  hash_key: z.string(),
  unit_type: experimentUnitTypeSchema,
  variations: z.array(variationSchema),
  rules: z.array(ruleSchema),
  phase: phaseSchema,
  surfaces: z.array(surfaceSchema),
  owning_team_id: z.string(),
  cached_notification_channel_id: z.string(),
  exposure_tracking: exposureTrackingSchema,
  assignment_mode: assignmentModeSchema,
  enable_edit_raw_json_ui: z.boolean(),
  winning_variation_id: z.number().int(),
  extra_outcome_context: z.string(),
  type: experimentTypeSchema,
  is_template: z.boolean(),
  field_numbers_to_copy: z.array(z.number().int()),
  engine_feature_flags: z.array(z.string()),
  debug_config: debugConfigSchema.optional(),
  expected_end_date: timestampSchema.optional(),
  is_automated_change: z.boolean(),
  suppress_editor_mention: z.boolean(),
  archive_at: timestampSchema.optional(),
  guild_experiment_version: z.number().int().optional(),
  custom_unit_prefix: z.number().int(),
  growthbook_tags: z.array(z.string()),
  allocate_right_to_left: z.boolean(),
  is_managed: z.boolean(),
  number_line_settings: numberLineSettingsSchema.optional(),
  eligibility_persistence: eligibilityPersistenceSchema,
});

export type ExperimentValidationResult = z.infer<typeof experimentSchema>;
